import {
  FieldsSelection,
  createClient,
  MutationGenqlSelection,
  Mutation,
} from '@rematter/frontend-lib-api-client';
import { atom, getDefaultStore, Getter, Setter } from 'jotai';
import { atomWithMutation } from 'jotai-tanstack-query';
import camelCase from 'lodash/camelCase';
import omit from 'lodash/omit';
import { v4 as uuidv4 } from 'uuid';

import { config } from '~/config';
import { $accessToken } from '~/shared/providers/auth/identity.localState';
import {
  $registerLoading,
  $unregisterLoading,
} from '~/shared/state/loading.state';

type MutationQueue = Array<() => Promise<unknown>>;

type MutationFactoryOptions<TData, TResult> = {
  alias?: string;
  select?: (data: TData) => TResult;
  retries?: number;
  skipQueue?: boolean;
  showLoadingOverlay?: boolean;
  mock?: TResult;
};

type CallbackParams<TVariables> = {
  requestId: string;
  params: TVariables;
};

type BeforeCallback<TVariables> = (
  get: Getter,
  set: Setter,
  params: CallbackParams<TVariables>,
) => void;

type OnSuccessCallback<TVariables, TResult> = (
  get: Getter,
  set: Setter,
  params: CallbackParams<TVariables> & { result: TResult },
) => void;

type OnErrorCallback<TVariables> = (
  get: Getter,
  set: Setter,
  params: CallbackParams<TVariables> & { error: Error },
) => void;

type MutationHookCallbacks<TVariables, TResult> = {
  before?: BeforeCallback<TVariables>;
  onSuccess?: OnSuccessCallback<TVariables, TResult>;
  onError?: OnErrorCallback<TVariables>;
};

const BASE_GRAPHQL_URL = `${config.api.url}/graphql`;
const getAccessToken = () => getDefaultStore().get($accessToken);

// Global mutation queue to ensure mutations are executed in order
const mutationQueue: MutationQueue = [];
let isProcessingQueue = false;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const executeWithRetry = async <T>(
  fn: () => Promise<T>,
  retries: number,
  attempt = 1,
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (
      attempt > retries ||
      !/failed to fetch/i.test((error as Error)?.message)
    ) {
      throw error;
    }

    // Exponential backoff: 1s, 2s, 4s, 8s...
    const delay = Math.pow(2, attempt - 1) * 2000;
    console.warn(
      `Mutation attempt ${attempt}/${retries} failed, retrying in ${delay}ms:`,
      error,
    );

    await sleep(delay);
    return executeWithRetry(fn, retries, attempt + 1);
  }
};

const processMutationQueue = async () => {
  if (isProcessingQueue) {
    return; // Queue is already being processed
  }

  isProcessingQueue = true;

  while (mutationQueue.length > 0) {
    const nextMutation = mutationQueue.shift();
    if (nextMutation) {
      try {
        await nextMutation();
      } catch (error) {
        // Error is already handled in the mutation wrapper
        console.warn('Mutation in queue failed after all retries:', error);
      }
    }
  }

  isProcessingQueue = false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const queueMutation = (mutationFn: () => Promise<any>) => {
  mutationQueue.push(mutationFn);
  processMutationQueue();
};

export const createMutationAtomFactory = <
  TSelection extends MutationGenqlSelection,
  TVariables,
  TData = FieldsSelection<Mutation, TSelection>,
  TResult = TData,
>(
  key: `${string}.${string}`, // 'transportation.truckCreate'
  selectionFn: (variables: TVariables) => TSelection,
  options?: MutationFactoryOptions<TData, TResult>, // retries default: 5
) => {
  const mutationName = `mutation.${key}`;
  const retries = options?.retries ?? 5;
  const showOverlay =
    options?.showLoadingOverlay !== undefined
      ? options.showLoadingOverlay
      : true;

  return ({
    before,
    onSuccess,
    onError,
  }: MutationHookCallbacks<TVariables, TResult> = {}) => {
    const $ = atomWithMutation(() => ({
      mutationKey: [mutationName],
      mutationFn: async (input: TVariables) => {
        if (options?.mock) {
          return options.mock;
        }

        const accessToken = getAccessToken();

        const client = createClient({
          url: `${BASE_GRAPHQL_URL}?q=${mutationName}`,
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          credentials: 'include',
        });

        const result = await client.mutation({
          ...selectionFn(input),
          __name: camelCase(options?.alias || key),
        });

        return options?.select?.(result as TData) ?? (result as TResult);
      },
    }));

    const newAtom = atom(
      (get) => omit(get($), ['mutate', 'mutateAsync']),
      (get, set, params: TVariables) => {
        const requestId = uuidv4();

        // Register loading operation for global overlay
        set($registerLoading, {
          id: `mutation-${requestId}`,
          label: mutationName,
          timestamp: Date.now(),
          showOverlay,
        });

        // Execute before callback immediately
        before?.(get, set, { requestId, params });

        return new Promise<TResult>((resolve, reject) => {
          const queuedMutation = async () => {
            try {
              const result = await executeWithRetry(
                () => get($).mutateAsync(params),
                retries,
              );

              onSuccess?.(get, set, { requestId, result, params });
              resolve(result);
            } catch (error) {
              onError?.(get, set, {
                requestId,
                error: error as Error,
                params,
              });
              reject(error);
            } finally {
              // Always unregister loading operation when mutation completes
              set($unregisterLoading, `mutation-${requestId}`);
            }
          };

          if (options?.skipQueue) {
            queuedMutation();
          } else {
            // Queue the mutation to ensure order is respected
            queueMutation(queuedMutation);
          }
        });
      },
    );

    newAtom.debugLabel = `$mutation.${key}`;

    return newAtom;
  };
};
